import React, { useState } from 'react';
import { Button, Form, Input, Typography } from 'antd';
import { MailOutlined, WarningOutlined, CheckCircleOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import podsLogo from '@assets/pods_logo_1.png';
import makeApiRequest from '@apiCalls/makeApiRequest';
import { API_ENDPOINTS, MY_PODS_URL } from '@constants/apiEndpoints';
import * as styles from '@styles/militaryReauthorize.module.scss';
import type apiRequestConfig from '@interfaces/apiEndpoints.interface';

const { Title, Text } = Typography;

const MilitaryReauthorize: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<React.ReactNode | null>(null);
    const [countdown, setCountdown] = useState(15);

    const onFinish = async (values: any) => {
        setLoading(true);
        setErrorMsg(null);
        setSuccessMsg(null);
        try {
            const requestConfig = API_ENDPOINTS.REAUTHORIZE_MILITARY as apiRequestConfig;

            // Pass email as query param (second argument to url function is passed via third arg of makeApiRequest)
            const response = await makeApiRequest({}, requestConfig, values.email);

            // If the response status is 200/302, we might have a redirect URL or the browser already followed it.
            if (response.status === 200) {
                if (response.data?.redirectUrl) {
                    window.location.href = response.data.redirectUrl;
                } else if (response.data?.message === "Already verified") {
                    setSuccessMsg(
                        <>
                            Already verified. To manage your orders visit <a href={MY_PODS_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#297ed1', fontWeight: 600 }}>mypods.com</a>
                            <div style={{ marginTop: '16px', fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
                                You will be automatically redirected to MyPODS in <span style={{ fontWeight: 'bold', color: '#297ed1' }}>{countdown}</span> seconds.
                            </div>
                        </>
                    );
                }
            } else if (response.status === 302) {
                // Browsers usually handle 302 before it reaches here, but just in case:
                const location = response.headers?.location;
                if (location) {
                    window.location.href = location;
                }
            }
        } catch (error: any) {
            console.error('Reauthorize error:', error);
            if (error.response?.status === 410) {
                setErrorMsg('Your verification has expired. Please contact support team.');
            } else if (error.response?.status === 404) {
                setErrorMsg('No record found for this email address. Please check and try again.');
            } else if (error.response?.status === 302) {
                // Sometimes axios treats 302 as error if it can't follow (e.g. CORS)
                const location = error.response.headers?.location;
                if (location) {
                    window.location.href = location;
                } else {
                    setErrorMsg('A redirect occurred but we could not follow it. Please try again.');
                }
            } else {
                setErrorMsg(error.response?.data?.message || 'An error occurred. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (successMsg && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (successMsg && countdown === 0) {
            window.location.href = MY_PODS_URL;
        }
    }, [successMsg, countdown]);

    return (
        <section className={styles.loginBody}>
            <div className={styles.logoWeb}>
                <img src={podsLogo as any} alt="PODS Logo" />
            </div>

            <section className={styles.formLogin}>
                {successMsg ? (
                    <div className={styles.successBox}>
                        <CheckCircleOutlined className={styles.successIcon} />
                        <div className={styles.errorTextContainer}>
                            <span className={styles.successMessage}>{successMsg}</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles.govxIconContainer}>
                            <SafetyCertificateOutlined />
                        </div>

                        <Title className={styles.govxTitle}>
                            Complete Your Military Discount Setup
                        </Title>

                        <div className={styles.govxSubtitle}>
                            Finish connecting your GovX account to keep your discount active.
                        </div>

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            style={{ width: '100%' }}
                        >
                            <Form.Item
                                name="email"
                                label={<span className={styles.emailLabel}>PODS order email</span>}
                                rules={[
                                    { required: true, message: 'Please enter your email' },
                                    { type: 'email', message: 'Please enter a valid email' }
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined className={styles.inputPrefixIcon} />}
                                    placeholder="example@email.com"
                                    size="large"
                                    className={styles.emailInput}
                                />
                            </Form.Item>

                            <div className={styles.inputInstruction}>
                                Enter the email you used to book with PODS. This may be different from your GovX email. We'll connect them to confirm your military discount eligibility.
                            </div>

                            {errorMsg && (
                                <div className={styles.errorBox}>
                                    <WarningOutlined className={styles.errorIcon} />
                                    <div className={styles.errorTextContainer}>
                                        <span className={styles.errorMessage}>{errorMsg}</span>
                                    </div>
                                </div>
                            )}

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className={styles.govxVerifyBtn}
                                    loading={loading}
                                >
                                    Connect accounts
                                </Button>
                            </Form.Item>

                            <div className={styles.govxFooterNote}>
                                You'll confirm your GovX account on the next screen. This process is secure and takes less than 2 minutes.
                            </div>
                        </Form>
                    </>
                )}

                {successMsg && (
                    <Button
                        type="link"
                        onClick={() => {
                            setSuccessMsg(null);
                            form.resetFields();
                        }}
                        style={{ width: '100%', marginTop: '16px' }}
                    >
                        Use a different email
                    </Button>
                )}
            </section>
        </section>
    );
};

export default MilitaryReauthorize;
